import {
  Box,
  Typography as Typo,
  Card as MuiCard,
  styled,
  TypographyProps,
  Link,
  Chip as MuiChip,
  FormControl as MuiFormControl,
  FormHelperText as MuiFormHelperText,
  Skeleton,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useFormik} from 'formik';
import {Button, Input} from '@jobseeker/lotus/elements';
import React, {useEffect, useState} from 'react';
import {CrossWhite} from '@jobseeker/lotus/assets/icons';
import SwitchWithHeading from '@jobseeker/lotus/components/SwitchWithHeading';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {saveUserSettings} from '@jobseeker/common-core/reducers/user/thunkServices';
import {validationSchemaBlockedCompanies} from '../../../helpers/formValidator';
import userService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';

interface BlockedCompany {
  name: string;
  id: number;
}

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
  bottom: -18,
}));

const CustomFormControl = styled(MuiFormControl)(({theme}) => ({
  '& .MuiInputBase-root': {
    '&:focus': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
    '&:hover': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
    },
    '& textarea:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
    },
    '& select:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
    },
    '&.Mui-error': {
      border: `0px solid ${theme.palette.error.main}`,
      borderRadius: '32px',
    },
  },
  '& .MuiInputBase-input': {
    height: '100%',
    outline: 'none !important',
    boxShadow: 'none !important',
    '&:focus': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
    '&:hover': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
  },
}));

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));

const CustomChip = styled(MuiChip)(() => ({
  background:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.70) 0%, rgba(255, 255, 255, 0.70) 100%), #CBD5E1',
  padding: '6px 12px 6px 0px',
}));

const Typography = styled(Typo)<TypographyProps>(({}) => ({
  display: 'flex',
  alignItems: 'center',
}));

const BlockCompaniesComponent: React.FC = () => {
  const [items, setItems] = useState<BlockedCompany[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [showMore, setShowMore] = useState(false);
  const visibleItems = showMore ? items : items.slice(0, 8);

  useEffect(() => {
    const fetchBlockedCompanies = async () => {
      try {
        const response = await userService.getBlockedCompanies();
        if (response.error) {
          enqueueSnackbar(response.error, {variant: 'error'});
          return;
        }
        const newData = response.data.map((item: BlockedCompany) => ({
          name: item.name,
          id: item.id,
        }));
        setItems(newData);
      } catch (error) {
        console.log(error);
      }
    };

    fetchBlockedCompanies();
  }, []);

  const handleBlockCompany = async (
    name: string,
    action: 'block' | 'unblock'
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = await userService.blockOrUnblockCompany({
      name,
      action,
    });
    if (response.error) {
      enqueueSnackbar(response.error, {variant: 'error'});
      return;
    }
    const obj = response.data;
    if (action === 'block') {
      setItems(prevItems => [...prevItems, obj]);
      enqueueSnackbar(response?.status?.message, {variant: 'success'});
    } else {
      setItems(prevItems => prevItems.filter(comp => comp.id != obj.id));
      enqueueSnackbar(response?.status?.message, {variant: 'success'});
    }
    console.log(obj);
  };

  const formik = useFormik({
    initialValues: {
      blockedCompany: '',
    },
    validationSchema: validationSchemaBlockedCompanies,
    onSubmit: values => {
      handleBlockCompany(values.blockedCompany, 'block');
      formik.resetForm();
    },
  });

  const isInputEmpty = formik.values.blockedCompany.trim() === '';
  const hasError =
    formik.touched.blockedCompany && Boolean(formik.errors.blockedCompany);

  return (
    <Box
      color="gray.dark"
      data-testid="notification_list_item"
      sx={{
        width: '100%',
        position: 'relative',
        transition: 'all ease 0.2s',
        // borderBottom: 'solid 1px',
        // borderColor: 'border.lightGray',
      }}
    >
      <Box
        sx={{
          py: {xxs: 1.5, md: 2},
          px: {xxs: 1.5, md: 2},
        }}
      >
        <Box
          display={'flex'}
          width={'100%'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Box width={'100%'} display={'flex'} flexDirection={'column'}>
            <Typography
              variant="subtitle2"
              color={'#333333'}
              sx={{
                lineClamp: '3',
                '-webkit-line-clamp': '2',
                '-webkit-box-orient': 'vertical',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                wordWrap: 'break-word',
                display: 'block',
                alignItems: 'center',
              }}
            >
              Block Companies
            </Typography>
            <Typography
              variant="body2"
              fontSize={{xxs: 12, md: 14}}
              sx={{
                color: '#7E7E7E',
                lineClamp: '3',
                '-webkit-line-clamp': '2',
                '-webkit-box-orient': 'vertical',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                wordWrap: 'break-word',
                display: 'block',
                alignItems: 'center',
              }}
            >
              You may block certain companies from viewing your profile.
              Recruiters from blocked domains{' '}
              <span
                style={{
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                  pointerEvents: 'none',
                }}
              >
                (e.g. xyz@ibm.com)&nbsp;
              </span>
              will not have access. You may block as many companies as you like
            </Typography>

            <Typography
              color={'#333333'}
              component="p"
              fontSize="14px"
              fontWeight={400}
              display="inline-block"
              marginTop={'16px'}
              sx={{
                float: 'left',
                textAlign: 'left',
              }}
            >
              Type the company domain
            </Typography>
            <Box
              mt={1}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '40px',
                width: {xxs: '100%', md: '75%'},
              }}
              component={'form'}
              noValidate
              onSubmit={event => {
                event.preventDefault();
                formik.handleSubmit();
              }}
            >
              <CustomFormControl
                error={hasError}
                sx={{
                  width: {xxs: '222px', sm: '80%'},
                  marginRight: '12px',
                }}
              >
                <Input
                  variant={VersionVariants.VERSION_THREE}
                  id="blockedCompany"
                  size="small"
                  type="text"
                  name="blockedCompany"
                  placeholder="E.g: ibm.com"
                  value={formik.values.blockedCompany}
                  onChange={formik.handleChange}
                  sx={{
                    marginTop: '0px',
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: '40px',
                    },
                    '& .MuiInputBase-input': {
                      height: '100%',
                    },
                  }}
                  error={hasError}
                />
                {hasError && (
                  <FormHelperText error>
                    {formik.errors.blockedCompany}
                  </FormHelperText>
                )}
              </CustomFormControl>

              <Box display="flex" flexWrap="nowrap" alignItems="center">
                <Button
                  disabled={isInputEmpty || hasError}
                  variant="outlined"
                  label={'block_company'}
                  size="small"
                  data-testid="block_company"
                  sx={{
                    fontSize: '16px',
                    fontWeight: '500',
                    borderRadius: '32px',
                    width: '94px',
                    px: 3,
                    py: 1,
                    lineHeight: '24px',
                    '&.Mui-disabled': {
                      backgroundColor: '#00000029',
                      color: '#00000042',
                      border: 'none',
                    },
                  }}
                  type="submit"
                >
                  Block
                </Button>
              </Box>
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              mt={{xxs: 4, md: 5}}
              mb={1}
            >
              <Typography fontSize={12} fontWeight={500} color="#7E7E7E">
                Blocked Companies
              </Typography>
              {items.length > 0 ? (
                <Box
                  sx={{
                    maxWidth: {
                      xxs: 'calc(100vw - 0px)',
                      md: 'calc(100% - 75px)',
                    },
                    marginTop: '10px',
                    transition: 'all ease 0.3',
                    display: 'flex',
                    flexWrap: 'wrap',
                    overflow: 'scroll',
                    scrollbarWidth: 'none',
                    gap: 1,
                  }}
                >
                  {visibleItems.map((f, i) => (
                    <CustomChip
                      deleteIcon={
                        <Image
                          src={CrossWhite}
                          alt={'Close Icon'}
                          width={12}
                          height={12}
                          objectFit="cover"
                          style={{cursor: 'pointer'}}
                        />
                      }
                      key={`filterchip-filter-${i}`}
                      label={f.name}
                      onDelete={async () => {
                        await handleBlockCompany(f.name, 'unblock');
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography
                  fontSize="12px"
                  fontWeight={400}
                  color={'#333333'}
                  mt={1.5}
                >
                  No blocked company!
                </Typography>
              )}
              {items.length > 8 && (
                <Link
                  onClick={() => setShowMore(!showMore)}
                  sx={{
                    cursor: 'pointer',
                    m: 0,
                    ml: 0.35,
                    textDecoration: 'none',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    fontSize: '12px',
                    fontWeight: '500',
                    marginTop: '12px',
                  }}
                >
                  {showMore
                    ? 'Show Less'
                    : `+${items.length - visibleItems.length} more companies`}
                </Link>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const PrivacySettingWidget = (props: {loading: boolean}) => {
  const {loading} = props;
  const userSettings = useSelector(
    (state: AppState) => state.user.profile.settings
  );
  const isLarge = useIsLargeDevice();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: any = useDispatch();

  const saveSettings = (name: string, value: number) => {
    dispatch(
      saveUserSettings({
        [name]: value == 1 ? 0 : 1,
      })
    );
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 4,
          marginLeft: {md: 2},
          width: '100%',
        }}
        data-test-id="privacy_setting_container"
      >
        <Box>
          {isLarge && (
            <Typography
              display="flex"
              fontSize={{xxs: 16, md: 20}}
              fontWeight={{xxs: 600, md: 500}}
              m={2}
              mb={1}
              mt={3}
              sx={{
                wordWrap: 'break-word',
                textTransform: 'capitalize',
              }}
            >
              Privacy Settings
            </Typography>
          )}

          <Box mt={isLarge ? 0 : 3}>
            {loading ? (
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                px={{xxs: 1, md: 2}}
              >
                <Box width={'100%'}>
                  <Skeleton
                    variant="rounded"
                    width={'50%'}
                    height={16}
                    sx={{
                      mt: {xxs: 0, md: 1},
                      mb: 1,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={'30%'}
                    height={12}
                    sx={{
                      mt: {xxs: 0, md: 1},
                    }}
                  />
                </Box>
                <Skeleton
                  variant="rounded"
                  width={100}
                  height={25}
                  sx={{
                    mt: {xxs: 0, md: 1},
                    ml: {xxs: 2, md: 'none'},
                    mb: 1,
                  }}
                />
              </Box>
            ) : (
              <SwitchWithHeading
                heading={'Hide profile from every recruiter/employer'}
                description={
                  'Your profile will only be visible for the specific roles you apply for '
                }
                value={!!userSettings.hideResume}
                isPro={false}
                onChange={console.log}
                proFeature={false}
                onClick={() =>
                  saveSettings('hideResume', userSettings.hideResume)
                }
              />
            )}

            {loading ? (
              <Box minWidth={'100%'} px={{xxs: 1, md: 2}}>
                <Skeleton
                  variant="rounded"
                  width={'50%'}
                  height={16}
                  sx={{
                    mt: 3,
                    mb: 1,
                  }}
                />
                {[
                  ...new Array(5).fill('').map(() => (
                    <>
                      <Skeleton
                        variant="rounded"
                        width={'100%'}
                        height={10}
                        sx={{
                          my: 0.5,
                        }}
                      />
                    </>
                  )),
                ]}
                <Skeleton
                  variant="rounded"
                  width={190}
                  height={16}
                  sx={{
                    mt: 3,
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={'100%'}
                  height={40}
                  sx={{
                    borderRadius: 16,
                    mt: 1,
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={122}
                  height={8}
                  sx={{
                    mt: 2.3,
                    mb: 4,
                  }}
                />
              </Box>
            ) : (
              <BlockCompaniesComponent />
            )}
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default PrivacySettingWidget;
